import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, Chip, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Divider, Tabs, Tab, Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import { PayrollService } from '../../services/payroll.service';
import { IPayroll } from '../../interfaces/payroll.interface';
import { IPayrollPaymentStatus, ISalaryPayment } from '../../interfaces/salaryPayment';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { UserType } from '../../interfaces/userType.enum';
import { displayUsername } from '../../utils/userUtils';
import PaymentStatusChip from './PaymentStatusChip';
import PaymentFormDialog from './PaymentFormDialog';
import PaymentReversalDialog from './PaymentReversalDialog';
import SkipJournalDialog from './SkipJournalDialog';
import { useTranslation } from 'react-i18next';

type MonthNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index}>
        {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
);

const PayrollDetail: React.FC = () => {
    const { t } = useTranslation(['payroll', 'common']);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [payroll, setPayroll] = useState<IPayroll | null>(null);
    const [tab, setTab] = useState(0);
    const [paymentStatus, setPaymentStatus] = useState<IPayrollPaymentStatus | null>(null);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [reverseTargetId, setReverseTargetId] = useState<string | null>(null);
    const [skipDialogOpen, setSkipDialogOpen] = useState(false);
    const [skipPaymentId, setSkipPaymentId] = useState<string | null>(null);
    const { showSuccess, showApiError } = useToast();
    const { user } = useAuth();

    const userRoles = user && user.roles ? (Array.isArray(user.roles) ? user.roles : [user.roles]) : [];
    const isInstitutionAdmin = userRoles.includes(UserType.InstitutionAdmin);

    const canCreatePayment = usePermission('PAYROLL_PAYMENT_CREATE', isInstitutionAdmin);
    const canReversePayment = usePermission('PAYROLL_PAYMENT_REVERSE', isInstitutionAdmin);
    const canOverride = usePermission('PAYROLL_PAYMENT_OVERRIDE', isInstitutionAdmin);

    const loadPayroll = useCallback(async () => {
        try {
            const data = await PayrollService.getPayrollById(id!);
            setPayroll(data);
        } catch (error) {
            showApiError(error, t('payrollDetail.toast.loadFailed'));
        }
    }, [id, showApiError, t]);

    const loadPaymentStatus = useCallback(async () => {
        if (!id) return;
        try {
            const data = await PayrollService.getPayrollPaymentStatus(id);
            setPaymentStatus(data);
        } catch (error) {
            showApiError(error, t('payrollDetail.toast.loadPaymentStatusFailed'));
        }
    }, [id, showApiError, t]);

    useEffect(() => {
        if (id) loadPayroll();
    }, [id, loadPayroll]);

    useEffect(() => {
        if (tab === 1 && id) loadPaymentStatus();
    }, [tab, id, loadPaymentStatus]);

    const handleDownload = async () => {
        try {
            await PayrollService.downloadPayslip(id!);
        } catch (error) {
            showApiError(error, t('shared.downloadPayslipFailed'));
        }
    };

    const handlePaymentSuccess = () => {
        loadPaymentStatus();
    };

    const handleRetryPost = async (paymentId: string) => {
        try {
            await PayrollService.retryJournalPost(paymentId);
            showSuccess(t('shared.journalRetrySucceeded'));
            loadPaymentStatus();
        } catch (error) {
            showApiError(error, t('shared.retryFailed'));
        }
    };

    const handleOpenSkip = (paymentId: string) => {
        setSkipPaymentId(paymentId);
        setSkipDialogOpen(true);
    };

    const handleCloseSkip = () => {
        setSkipDialogOpen(false);
        setSkipPaymentId(null);
    };

    if (!payroll) return <Box sx={{ p: 3 }}><Typography>{t('shared.loading')}</Typography></Box>;

    const totalAllDeductions = payroll.totalDeductions + payroll.unpaidLeaveDeduction + payroll.absenceDeduction;
    const isFinalized = payroll.status === 'FINALIZED';

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/payroll/run')}>
                    {t('common:actions.back')}
                </Button>
                <Typography variant="h5">{t('payrollDetail.title')}</Typography>
                <Chip label={t(`payrollStatus.${payroll.status}`)} color={isFinalized ? 'success' : 'warning'} />
                <Box sx={{ flexGrow: 1 }} />
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
                    {t('payrollDetail.downloadPayslip')}
                </Button>
            </Box>

            <Paper sx={{ mb: 3 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                    <Tab label={t('payrollDetail.tabDetails')} />
                    <Tab label={t('payrollDetail.tabPayments')} />
                </Tabs>
            </Paper>

            <TabPanel value={tab} index={0}>
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {t('payrollDetail.heading', {
                            name: displayUsername(payroll.userName),
                            month: t(`common:months.${payroll.month as MonthNumber}`),
                            year: payroll.year,
                        })}
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>{t('payrollDetail.earnings')}</Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('shared.component')}</TableCell>
                                    <TableCell align="right">{t('common:fields.amount')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {payroll.earningsBreakdown && Object.entries(payroll.earningsBreakdown).map(([name, amount]) => (
                                    <TableRow key={name}>
                                        <TableCell>{name}</TableCell>
                                        <TableCell align="right">{Number(amount).toFixed(2)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{t('payrollDetail.totalEarnings')}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{payroll.grossSalary.toFixed(2)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>{t('payrollDetail.deductions')}</Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('shared.component')}</TableCell>
                                    <TableCell align="right">{t('common:fields.amount')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {payroll.deductionsBreakdown && Object.entries(payroll.deductionsBreakdown).map(([name, amount]) => (
                                    <TableRow key={name}>
                                        <TableCell>{name}</TableCell>
                                        <TableCell align="right">{Number(amount).toFixed(2)}</TableCell>
                                    </TableRow>
                                ))}
                                {payroll.unpaidLeaveDays > 0 && (
                                    <TableRow>
                                        <TableCell>{t('payrollDetail.unpaidLeave', { count: payroll.unpaidLeaveDays })}</TableCell>
                                        <TableCell align="right">{payroll.unpaidLeaveDeduction.toFixed(2)}</TableCell>
                                    </TableRow>
                                )}
                                {payroll.absentDays > 0 && (
                                    <TableRow>
                                        <TableCell>{t('payrollDetail.absence', { count: payroll.absentDays })}</TableCell>
                                        <TableCell align="right">{payroll.absenceDeduction.toFixed(2)}</TableCell>
                                    </TableRow>
                                )}
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{t('payrollDetail.totalDeductions')}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totalAllDeductions.toFixed(2)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="h6">{t('shared.netSalary')}</Typography>
                        <Typography variant="h6" color="primary">{payroll.netSalary.toFixed(2)}</Typography>
                    </Box>
                </Paper>
            </TabPanel>

            <TabPanel value={tab} index={1}>
                {!paymentStatus ? (
                    <Typography>{t('payrollDetail.loadingPaymentStatus')}</Typography>
                ) : (
                    <>
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">{t('shared.netSalary')}</Typography>
                                    <Typography variant="h6">{paymentStatus.netSalary.toFixed(2)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">{t('payrollDetail.totalPaid')}</Typography>
                                    <Typography variant="h6">{paymentStatus.totalPaid.toFixed(2)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">{t('payrollDetail.outstanding')}</Typography>
                                    <Typography variant="h6" color={paymentStatus.outstanding > 0 ? 'warning.main' : 'text.primary'}>
                                        {paymentStatus.outstanding.toFixed(2)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Chip
                                        label={paymentStatus.fullyPaid ? t('payrollDetail.fullyPaid') : t('payrollDetail.notFullyPaid')}
                                        color={paymentStatus.fullyPaid ? 'success' : 'warning'}
                                    />
                                </Box>
                                <Box sx={{ flexGrow: 1 }} />
                                {canCreatePayment && isFinalized && (
                                    <Button
                                        variant="contained"
                                        onClick={() => setPaymentDialogOpen(true)}
                                    >
                                        {t('payrollDetail.recordPayment')}
                                    </Button>
                                )}
                            </Stack>
                        </Paper>

                        <Paper sx={{ p: 3 }}>
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>{t('payrollDetail.paymentHistory')}</Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('common:fields.date')}</TableCell>
                                            <TableCell align="right">{t('common:fields.amount')}</TableCell>
                                            <TableCell>{t('shared.method')}</TableCell>
                                            <TableCell>{t('common:fields.status')}</TableCell>
                                            <TableCell>{t('common:fields.reason')}</TableCell>
                                            <TableCell align="right">{t('common:fields.actions')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paymentStatus.payments.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        {t('payrollDetail.noPayments')}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {paymentStatus.payments.map((p: ISalaryPayment) => {
                                            const canReverse = canReversePayment
                                                && p.status === 'COMPLETED'
                                                && !p.reversesPaymentId;
                                            const needsJournalAction = p.journalPostStatus === 'FAILED' || p.journalPostStatus === 'SKIPPED';
                                            return (
                                                <TableRow key={p.id}>
                                                    <TableCell>{p.paymentDate}</TableCell>
                                                    <TableCell align="right">{Number(p.amount).toFixed(2)}</TableCell>
                                                    <TableCell>{t(`paymentMethod.${p.paymentMethod}`)}</TableCell>
                                                    <TableCell>
                                                        <PaymentStatusChip
                                                            status={p.status}
                                                            journalPostStatus={p.journalPostStatus}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{p.reason || '-'}</TableCell>
                                                    <TableCell align="right">
                                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                            {canReverse && (
                                                                <Button
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => setReverseTargetId(p.id)}
                                                                >
                                                                    {t('shared.reverse')}
                                                                </Button>
                                                            )}
                                                            {needsJournalAction && canCreatePayment && (
                                                                <Button
                                                                    size="small"
                                                                    onClick={() => handleRetryPost(p.id)}
                                                                >
                                                                    {t('shared.retryPost')}
                                                                </Button>
                                                            )}
                                                            {needsJournalAction && canOverride && (
                                                                <Button
                                                                    size="small"
                                                                    color="warning"
                                                                    onClick={() => handleOpenSkip(p.id)}
                                                                >
                                                                    {t('shared.skip')}
                                                                </Button>
                                                            )}
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </>
                )}
            </TabPanel>

            {paymentStatus && (
                <PaymentFormDialog
                    open={paymentDialogOpen}
                    onClose={() => setPaymentDialogOpen(false)}
                    payrollId={id!}
                    netSalary={paymentStatus.netSalary}
                    outstanding={paymentStatus.outstanding}
                    onSuccess={handlePaymentSuccess}
                />
            )}

            <PaymentReversalDialog
                open={reverseTargetId !== null}
                onClose={() => setReverseTargetId(null)}
                paymentId={reverseTargetId || ''}
                onSuccess={handlePaymentSuccess}
            />

            <SkipJournalDialog
                open={skipDialogOpen}
                onClose={handleCloseSkip}
                paymentId={skipPaymentId || ''}
                onSuccess={handlePaymentSuccess}
            />
        </Box>
    );
};

export default PayrollDetail;
